import { format, fromUnixTime } from "date-fns";
import { useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { MatchParams } from ".";
import { BookingDetails } from "../../../components/Brand/BookingDetails";
import { Card } from "../../../components/Card";
import { Modal } from "../../../components/Modal";
import { Column, Table } from "../../../components/Table";
import { Tag } from "../../../components/Tag";
import { Text } from "../../../components/Text";
import { View } from "../../../components/View";
import { BookingStatus, BrandBookingsQuery } from "../../../graphql/generated";
import styled from "../../../styles";

interface Props {
  bookings: BrandBookingsQuery["bookings"]["bookings"];
  lastElementRef: (node: any) => void;
  isFetching: boolean;
}

const Wrap = styled.div`
  height: 100%;
  padding: 0 0 ${(p) => p.theme.spacing.xl};
`;

export const ArchivedBookings = ({
  bookings,
  lastElementRef,
  isFetching,
}: Props) => {
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null);
  let idMatch = useRouteMatch<MatchParams>("/b/bookings/:page/:id");
  const history = useHistory();

  useEffect(() => {
    setActiveBookingId(idMatch && idMatch.params.id ? idMatch.params.id : null);
  }, [idMatch]);

  const columns: Column<BrandBookingsQuery["bookings"]["bookings"][0]>[] = [
    {
      header: "Status",
      key: "status",
      width: 130,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text" isCompact>
          {booking.status === BookingStatus.BookingStatusCancelled ? (
            <Tag text="Cancelled" color="purple" />
          ) : booking.status === BookingStatus.BookingStatusExpired ? (
            <Tag text="Expired" color="blue" />
          ) : (
            <Tag text="Rejected" color="red" />
          )}
        </Text>
      ),
    },
    {
      header: "Name",
      key: "name",
      width: 160, // Using wider column width as per original
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text" isCompact truncate>
          {booking.creator.preferredProfile?.name}
        </Text>
      ),
    },
    {
      header: "Handle",
      key: "handle",
      width: 130,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text" isCompact truncate>
          {booking.creator.preferredProfile?.username}
        </Text>
      ),
    },
    {
      header: "Listing",
      key: "listing",
      width: 180,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text" isCompact truncate>
          {booking.listing.name}
        </Text>
      ),
    },
    {
      header: "Location",
      key: "location",
      width: 180,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text" isCompact truncate>
          {booking.location?.name}
        </Text>
      ),
    },
    {
      header: "Expiry Date",
      key: "expirationDate",
      width: 130,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text">
          {booking.status === BookingStatus.BookingStatusExpired &&
          booking.updatedAt
            ? format(fromUnixTime(booking.updatedAt), "dd/MM/yyyy")
            : ""}
        </Text>
      ),
    },
    {
      header: "Cancellation Date",
      key: "cancellationDate",
      width: 180,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text">
          {booking.status === BookingStatus.BookingStatusCancelled &&
          booking.updatedAt
            ? format(fromUnixTime(booking.updatedAt), "dd/MM/yyyy")
            : ""}
        </Text>
      ),
    },
    {
      header: "Rejected At",
      key: "rejectedAt",
      width: 130,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text">
          {booking.status === BookingStatus.BookingStatusRejected &&
          booking.updatedAt
            ? format(fromUnixTime(booking.updatedAt), "dd/MM/yyyy")
            : ""}
        </Text>
      ),
    },
    {
      header: "Application Date",
      key: "applicationDate",
      width: 180,
      render: (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => (
        <Text margin="0" colorPreset="text">
          {booking.createdAt
            ? format(fromUnixTime(booking.createdAt), "dd/MM/yyyy")
            : ""}
        </Text>
      ),
    },
  ];

  return (
    <Wrap>
      <Modal
        isOpen={activeBookingId ? true : false}
        setIsOpen={() => setActiveBookingId(null)}
        onClose={() => {
          history.replace("/b/bookings/archived");
        }}
        maxWidth={960}
        noPadding
        zIndex={950} // less than 999 to allow for modal stack
      >
        <BookingDetails placement="modal" bookingId={activeBookingId} />
      </Modal>

      <View padding="0 0 xl">
        <Card>
          <Table
            isFetching={isFetching}
            columns={columns}
            data={bookings?.filter((e) => e.creator.preferredProfile) || []}
            onRowClick={(booking) =>
              history.push(`/b/bookings/archived/${booking.id}`)
            }
            lastElementRef={lastElementRef}
          />
        </Card>
      </View>
    </Wrap>
  );
};
